import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router";
import { LinkIcon, MailCheck } from "lucide-react";
import { sessionApi } from "../../api/account";
import { AuthBadge, AuthLayout } from "../../components/account/AuthLayout";
import { usePageTitle } from "../catalogue/usePageTitle";
import { errorMessage, SESSION_KEY, useCustomerSession } from "./session";

/** Where the confirmation email's link lands. Confirms the address; it doesn't sign in. */
export function ConfirmEmailRoute() {
  usePageTitle("Confirm your email");
  const queryClient = useQueryClient();
  const session = useCustomerSession();
  const token = useSearchParams()[0].get("token") ?? "";
  const confirm = useMutation({
    mutationFn: () => sessionApi.confirmEmail(token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
  });

  // Once only: the link works once, and development renders effects twice.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !token) return;
    started.current = true;
    confirm.mutate();
  }, [confirm, token]);

  if (confirm.isSuccess) {
    const signedIn = Boolean(session.data?.user);
    return (
      <AuthLayout>
        <AuthBadge icon={MailCheck} />
        <h1 className="mb-4 type-h1 [font-size:var(--text-2xl)] tracking-tight">Your email is confirmed</h1>
        <p className="mb-7 type-body text-text-body">Order confirmations and payment details will reach you there.</p>
        <Link to={signedIn ? "/" : "/sign-in"} className="inline-flex min-h-11 items-center border-b-0 type-body font-semibold">
          {signedIn ? "Continue shopping" : "Sign in"}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {confirm.isError || !token ? (
        <>
          <AuthBadge icon={LinkIcon} tone="danger" />
          <h1 className="mb-4 type-h1 [font-size:var(--text-2xl)] tracking-tight">This link didn't work</h1>
          <p className="mb-7 type-body text-text-body">
            {(token ? errorMessage(confirm.error) ?? "" : "This link is incomplete").replace(/\.$/, "")}. If your email is already confirmed, there's nothing more to
            do. Otherwise, send a new link from your account page.
          </p>
          <Link to="/account" className="inline-flex min-h-11 items-center border-b-0 type-body font-semibold">
            Go to your account
          </Link>
        </>
      ) : (
        <p role="status" className="type-body text-text-muted">
          Confirming your email…
        </p>
      )}
    </AuthLayout>
  );
}
